import { useMemo, useState } from 'react';
import { gql } from '@apollo/client';
import _ from 'lodash';
import MetricsSummary from '../components/economics/MetricsSummary';
import YIELD_METRICS from '../constants/yieldMetrics';
import LOSS_EVENT_METRICS from '../constants/lossEventMetrics';
import {
  useDataTransforms,
  useFarmName,
  useFilterYears,
  useStartYear,
} from '../utils/hooks';
import useFarmOrSectionGroupData from '../hooks/useFarmOrSectionGroupData';
import { useUnitsMap } from '../contexts/UnitContext';
import { max } from '../utils/aggregators';
import { metricsQueryString } from '../utils/utils';
import { asQuantityWithUnit } from '../utils/formatters';
import ItemsTable from '../components/tables/ItemsTable';
import { colorForCrop } from '../utils/colors';
import { ControlledTabs } from '../components/basics/Tabs';
import MetricsHeader from '../components/layout/MetricsHeader';
import SummarySection from '../components/layout/SummarySection';
import DetailsSection from '../components/layout/DetailsSection';
import { N_YEARS } from '../constants/years';
import Content from '../components/layout/Content';
import { CropDisplayFragment, YieldAggregatesEconFragment } from '../queries';
import { YIELD_UNITS_BY_DEFAULT } from '../constants/units';
import { getYieldData } from '../utils/transforms';

const TABS = [
  { display: 'Crop Yield Growth', value: 'yield' },
  { display: 'Weather, Pest, and Mortality Adjustments', value: 'adjustments' },
];

const FRAGMENT_NAME = 'YieldEconFragment';
const QUERY_NAME = 'YieldEconQuery';

const YieldFragment = gql`
  ${CropDisplayFragment}
  ${YieldAggregatesEconFragment}
  fragment ${FRAGMENT_NAME} on SectionsAggregate {
    cropAggregates {
      crop {
        ...CropDisplayFields
        lossEvents {
          id
          ${metricsQueryString(LOSS_EVENT_METRICS)}
        }
      }
      yieldAggregates {
        ...YieldAggregateFields
      }
    }
  }
`;

const toSummaryData = ({ cropAggregates }) =>
  cropAggregates.reduce(
    (result, { crop: { name }, yieldByYear }) =>
      Object.assign(result, { [name]: yieldByYear }),
    {},
  );

const getSummaryMetrics = ({ cropAggregates }) => {
  const counts = Object.entries(_.countBy(cropAggregates, (ca) => ca.unit));
  const unitWithMostCrops =
    counts.length === 0 ? '' : _.maxBy(counts, (item) => item[1])[0];

  return cropAggregates.map(({ crop, unit }) => ({
    displayName: crop?.name,
    accessor: crop?.name,
    aggregator: max,
    color: colorForCrop(crop),
    defaultShown: unit === unitWithMostCrops,
    format: asQuantityWithUnit(unit),
    info: `(Max ${unit}./yr)`,
    yAxisId: unit,
    unit,
  }));
};

const getLossEvents = (cropAggregate) => cropAggregate.crop.lossEvents;

const YieldView = () => {
  const filterYears = useFilterYears();
  const startYear = useStartYear();
  const farmName = useFarmName();

  const yieldUnitsMap = useUnitsMap(YIELD_UNITS_BY_DEFAULT);

  const { msg, data } = useFarmOrSectionGroupData(
    YieldFragment,
    QUERY_NAME,
    FRAGMENT_NAME,
  );

  const convertedData = useMemo(
    () => data && getYieldData(yieldUnitsMap)(data),
    [data, yieldUnitsMap],
  );

  const [summaryData, summaryMetrics] = useDataTransforms(convertedData, [
    toSummaryData,
    getSummaryMetrics,
  ]);

  const getYield = (yieldData) => {
    const { targetYieldPerTree, yearOfMaturity, ...rest } = yieldData;

    return [
      {
        ...rest,
        targetYieldPerTree,
        yearOfMaturity:
          targetYieldPerTree === 0
            ? ''
            : yearOfMaturity === null
            ? `>${startYear + N_YEARS}`
            : yearOfMaturity,
      },
    ];
  };

  const [activeTab, setActiveTab] = useState(TABS[0].value);

  return (
    <>
      <MetricsHeader showCumulativeYearSelect showAnnualSingleYearSelect>
        Yield
      </MetricsHeader>
      <Content>
        <SummarySection>
          <MetricsSummary
            metrics={summaryMetrics}
            data={summaryData}
            arrayTransform={filterYears}
            msg={msg}
          />
        </SummarySection>
        <DetailsSection>
          <ItemsTable
            data={convertedData}
            cols={activeTab === 'yield' ? YIELD_METRICS : LOSS_EVENT_METRICS}
            msg={msg}
            getItems={activeTab === 'yield' ? getYield : getLossEvents}
            byCrop
            tableHeader={
              <ControlledTabs
                activeTab={activeTab}
                tabs={TABS}
                onTabChange={setActiveTab}
              />
            }
            csvFileName={[activeTab, farmName].join(' - ')}
          />
        </DetailsSection>
      </Content>
    </>
  );
};

export default YieldView;
